/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, WritableAtom, atom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { Layout } from 'react-grid-layout';
import {
  activeCompetitorGroupHighlightedVehiclesSelector,
  activeCompetitorGroupVehiclesSelector,
} from 'jotai/competitorGroup';
import {
  activeSeriesDriversSelector,
  playoffDriverAtomFamily,
  seriesUniqueVehicleNumbersSelectorFamily,
  unwrappedOemBySeriesSelector,
  unwrappedDriverNamesSelector,
} from 'jotai/driver';
import { addOrUpdateEntityItem, removeEntityId } from 'jotai/entity';
import {
  currentFasterDriverSelectorFamily,
  lapDataLuckyDogSelectorFamily,
  leaderVehicleNumbersSelectorFamily,
} from 'jotai/lapData';
import { selectedReferenceVehiclesColorSelectorFamily } from 'jotai/referenceVehicle';
import { activeSeriesIdSelector, activeSessionReplayEnabledSelector, activeSessionSelector } from 'jotai/session';
import { HighlightObject, getDefaultVehicleColor } from 'jotai/vehicleColor';
import { viewAtomFamily } from 'jotai/view';
import { viewLayoutsAtomFamily } from 'jotai/viewLayouts';
import {
  LocalVehicleSelections,
  LocalVehicleSelectionsObject,
  VisualizationSettingsObject,
  getPerformanceColor,
  getVisualizationSettingsDefault,
  visualizationSettingIdsAtom,
  visualizationSettingsAtomFamily,
} from 'jotai/visualizationSettings';
import { activeWorkspaceAtom } from 'jotai/workspace';
import { workspaceService } from 'services/workspace/WorkspaceService';
import { v4 as uuidv4 } from 'uuid';
import { VisualizationObject, getVisualizationLayoutDefault } from '.';
import { OriginType } from 'models/viewModels';
import { getPlayoffDriversColor } from 'jotai/visualizationSettings/PlayoffDriverColorObject';

// https://github.com/react-grid-layout/react-grid-layout/blob/5126e7be9a1e38053e078f2b17e1a0f3a7abcda0/lib/utils.js#L87
export const bottom = (layout: Layout[]): number => {
  let max = 0,
    bottomY;
  for (let i = 0, len = layout.length; i < len; i++) {
    bottomY = layout[i].y + layout[i].h;
    if (bottomY > max) max = bottomY;
  }
  return max;
};

export const visualizationIdsAtom = atom<string[], [string[]], void>(
  (get) => {
    const currentWorkspace = get(activeWorkspaceAtom);
    if (Object.keys(currentWorkspace).length > 0) {
      return [...currentWorkspace.workspace.visualizations.allIds];
    } else {
      return [];
    }
  },
  (get, set, newValue: string[]) => {
    const previousWorkspace = get(activeWorkspaceAtom);
    previousWorkspace.workspace.visualizations.allIds = newValue;
    set(activeWorkspaceAtom, { ...previousWorkspace });
  }
);

export const visualizationsAtomFamily = atomFamily<
  string,
  WritableAtom<VisualizationObject, [VisualizationObject], void>
>((id: string) =>
  atom<VisualizationObject, [VisualizationObject], void>(
    (get) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      if (Object.keys(currentWorkspace).length > 0) {
        return currentWorkspace.workspace.visualizations.byId[id];
      } else {
        return { id: '', name: '', type: '', settings: '' };
      }
    },
    (get, set, visualization: VisualizationObject) => {
      const currentWorkspace = get(activeWorkspaceAtom);
      if (Object.keys(visualization).length === 0) {
        removeEntityId(currentWorkspace.workspace.visualizations, id);
        set(activeWorkspaceAtom, { ...currentWorkspace });
      } else {
        addOrUpdateEntityItem(currentWorkspace.workspace.visualizations, visualization, visualization.id);
        set(activeWorkspaceAtom, { ...currentWorkspace });
        workspaceService.updateWorkspace(currentWorkspace.workspace);
      }
    }
  )
);

export const useDeleteVisualization = (viewId: string, visualizationId: string) => {
  return useAtomCallback(
    useCallback(
      (get, set) => {
        const visualization = get(visualizationsAtomFamily(visualizationId));

        // Reset (delete) Visualization atom.
        set(visualizationsAtomFamily(visualizationId), {} as VisualizationObject);

        // Remove Visualization id from list.
        const previousVisualizationIds = get(visualizationIdsAtom);
        set(
          visualizationIdsAtom,
          previousVisualizationIds.filter((id) => id !== visualizationId)
        );

        // Remove Visualization id from its parent View.
        const previousView = get(viewAtomFamily(viewId));
        const newValue = {
          ...previousView,
          visualizations: previousView.visualizations.filter((id) => id !== visualizationId),
        };
        set(viewAtomFamily(viewId), newValue);

        // Reset (delete) Visualization Settings atom.
        set(visualizationSettingsAtomFamily(visualization.settings), {} as VisualizationSettingsObject);

        // Remove Visualization Settings id from list.
        const previousVisualizationSettingsId = get(visualizationSettingIdsAtom);
        set(
          visualizationSettingIdsAtom,
          previousVisualizationSettingsId.filter((id) => id !== visualization.settings)
        );
      },
      [viewId, visualizationId]
    )
  );
};

export const useAddVisualization = (viewId: string, visualization: VisualizationObject) => {
  return useAtomCallback(
    useCallback(
      (get, set) => {
        const id = uuidv4();
        const settingsId = uuidv4();
        const view = get(viewAtomFamily(viewId));
        const session = get(activeSessionSelector);
        const visualizationSettingsIds = get(visualizationSettingIdsAtom);
        const previousViewLayouts = get(viewLayoutsAtomFamily(view.layouts));
        const previousVisualizationIds = get(visualizationIdsAtom);

        // Add Visualization Settings id to list.
        set(visualizationSettingIdsAtom, [...visualizationSettingsIds, settingsId]);

        // Add Visualization Settings atom.
        set(
          visualizationSettingsAtomFamily(settingsId),
          getVisualizationSettingsDefault(settingsId, visualization.type, session?.sessionType)
        );

        // Modify Layouts so that a default size can be provided.
        const layout = getVisualizationLayoutDefault(id, visualization.type);
        layout.y = bottom(previousViewLayouts);
        const newValue = [...previousViewLayouts, layout];
        set(viewLayoutsAtomFamily(view.layouts), { viewLayouts: newValue, isReset: false });

        // Add Visualization id to list.
        set(visualizationIdsAtom, [...previousVisualizationIds, id]);

        // Add Visualization atom.
        set(visualizationsAtomFamily(id), { ...visualization, id, settings: settingsId });

        // Add Visualization to its parent View.
        set(viewAtomFamily(viewId), {
          ...view,
          visualizations: [...view.visualizations, id],
        });
      },
      [viewId, visualization]
    )
  );
};

const localVehicleSelectionsSelectorFamily = atomFamily<string, Atom<LocalVehicleSelections | null>>(
  (settingsId: string) =>
    atom((get) => {
      const settings = get(visualizationSettingsAtomFamily(settingsId));
      return settings &&
        Object.hasOwn(settings, 'localVehicleSelections') &&
        (settings as LocalVehicleSelectionsObject).localVehicleSelections.useLocalSelections
        ? (settings as LocalVehicleSelectionsObject).localVehicleSelections
        : null;
    })
);

export const vehicleColorsSelectorFamily = atomFamily<string, Atom<HighlightObject>>((settingsId: string) =>
  atom<HighlightObject>((get) => {
    const settings = get(visualizationSettingsAtomFamily(settingsId));
    const activeCompetitorGroupVehicles = get(activeCompetitorGroupHighlightedVehiclesSelector);
    const seriesId = get(activeSeriesIdSelector);
    const referenceVehicleObj = get(selectedReferenceVehiclesColorSelectorFamily(seriesId || ''));
    let activeVehicleNumbers: string[] = [];
    let selectedGroupColors = {};
    let selectedLeadersColors = {};
    let selectedCurrentFastestDriverColor = {};
    let newVehicleColors = {};
    let selectedLuckyDogColors = {};
    let selectedActivePlayoffDriversColors = {};
    let selectedAllPlayoffDriversColors = {};
    let referenceVehicleColor = {} as HighlightObject;

    if (seriesId) {
      activeVehicleNumbers = get(seriesUniqueVehicleNumbersSelectorFamily(seriesId));
    }

    if (referenceVehicleObj) {
      referenceVehicleColor[referenceVehicleObj?.vehicleNumber] = referenceVehicleObj?.colorId;
    }

    if (settings.localVehicleSelections?.useLocalSelections) {
      if (settings.localVehicleSelections?.selectedGroup === 'oem') {
        selectedGroupColors = get(selectedOemVehicleNumbersSelectorFamily(settingsId));
      } else if (settings.localVehicleSelections?.selectedGroup === 'performance') {
        if (settings.localVehicleSelections?.selectedPerformances.includes('leaders')) {
          selectedLeadersColors = get(selectedLeadersVehicleNumbersSelectorFamily(settingsId));
        }
        if (settings.localVehicleSelections?.selectedPerformances.includes('currentFastestDriver')) {
          selectedCurrentFastestDriverColor = get(selectedCurrentFastestDriverVehicleNumbersSelectorFamily(settingsId));
        }
        if (settings.localVehicleSelections?.selectedPerformances.includes('luckyDog')) {
          selectedLuckyDogColors = get(selectedLuckyDogSelectorFamily(settingsId));
        }
      } else if (settings.localVehicleSelections?.selectedGroup === 'playoff') {
        if (settings.localVehicleSelections?.selectedPlayoffDrivers.includes('active')) {
          selectedActivePlayoffDriversColors = get(
            selectedActivePlayoffDriverVehicleNumbersSelectorFamily({ settingsId: settingsId, numDriversToShow: 0 })
          );
        }
        if (settings.localVehicleSelections?.selectedPlayoffDrivers.includes('all')) {
          selectedAllPlayoffDriversColors = get(
            selectedActivePlayoffDriverVehicleNumbersSelectorFamily({
              settingsId: settingsId,
              numDriversToShow: settings.localVehicleSelections?.selectedPlayoffTopDrivers,
            })
          );
        }
      }
      const allActiveCompetitorGroupVehicles = get(activeCompetitorGroupVehiclesSelector);
      const individualSelectionsColors = (
        settings as LocalVehicleSelectionsObject
      )?.localVehicleSelections?.individualSelections?.reduce((previousValue, currentValue) => {
        if (activeVehicleNumbers.length > 0 && !activeVehicleNumbers.includes(currentValue)) {
          return previousValue;
        }
        const vehicle = allActiveCompetitorGroupVehicles.find((vehicle) => vehicle.vehicleNumber === currentValue);
        previousValue[currentValue] = vehicle?.colorId || getDefaultVehicleColor(currentValue, seriesId).id;
        return previousValue;
      }, {} as HighlightObject);

      newVehicleColors = {
        ...selectedGroupColors,
        ...selectedLuckyDogColors,
        ...referenceVehicleColor,
        ...selectedLeadersColors,
        ...selectedCurrentFastestDriverColor,
        ...selectedActivePlayoffDriversColors,
        ...selectedAllPlayoffDriversColors,
        ...individualSelectionsColors,
      };
    } else if (activeCompetitorGroupVehicles) {
      newVehicleColors = activeCompetitorGroupVehicles.reduce((ruleList, vehicle) => {
        if (activeVehicleNumbers.length > 0 && !activeVehicleNumbers.includes(vehicle.vehicleNumber)) {
          return ruleList;
        }
        ruleList[vehicle.vehicleNumber] = vehicle.colorId || getDefaultVehicleColor(vehicle.vehicleNumber, seriesId).id;
        return ruleList;
      }, {} as HighlightObject);
      newVehicleColors = { ...newVehicleColors, ...referenceVehicleColor };
    }
    return newVehicleColors;
  })
);

const selectedOemVehicleNumbersSelectorFamily = atomFamily<string, Atom<HighlightObject>>((settingsId: string) =>
  atom<HighlightObject>((get) => {
    const vehicles = get(activeSeriesDriversSelector);
    if (vehicles?.length) {
      const localVehicleSelections = get(localVehicleSelectionsSelectorFamily(settingsId));
      const oemColor = get(unwrappedOemBySeriesSelector);
      return vehicles.reduce((previousValue, currentValue) => {
        if (
          localVehicleSelections &&
          localVehicleSelections.selectedOems.find((item) => item === currentValue.manufacturer)
        ) {
          const vehicleColor = oemColor?.find((item) => item.manufacturer === currentValue.manufacturer)?.id;
          if (vehicleColor) previousValue[currentValue.vehicleNumber] = vehicleColor;
        }
        return previousValue;
      }, {} as HighlightObject);
    }
    return {};
  })
);

const selectedLeadersVehicleNumbersSelectorFamily = atomFamily<string, Atom<HighlightObject>>((settingsId: string) =>
  atom<HighlightObject>((get) => {
    const settings = get(visualizationSettingsAtomFamily(settingsId));
    const activeSeriesId = get(activeSeriesIdSelector);
    const activeCompetitorGroupVehicles = get(activeCompetitorGroupVehiclesSelector);
    if (settings && settings.localVehicleSelections?.selectedPerformances?.includes('leaders')) {
      const dataSource = settings.dataFeedSelections || OriginType.ERDP;
      let lowestPosition = 0;
      switch (settings?.localVehicleSelections?.selectedLeader) {
        case 'leaderOnly':
          lowestPosition = 1;
          break;
        case 'top3':
          lowestPosition = 3;
          break;
        case 'top5':
          lowestPosition = 5;
          break;
        case 'top10':
          lowestPosition = 10;
          break;
        default:
          lowestPosition = 0;
          break;
      }
      const vehicleNumbers = get(
        leaderVehicleNumbersSelectorFamily({ lowestLeadingPosition: lowestPosition, dataSource: dataSource })
      );
      const leadersColorId = getPerformanceColor('leaders').id;
      return vehicleNumbers.reduce((previousValue, currentValue, index) => {
        const competitorGroupVehicle = activeCompetitorGroupVehicles.find(
          (activeCompetitorGroupVehicles) => activeCompetitorGroupVehicles.vehicleNumber === currentValue
        );

        previousValue[currentValue] =
          index === 0
            ? leadersColorId
            : competitorGroupVehicle?.colorId || getDefaultVehicleColor(currentValue, activeSeriesId).id;

        return previousValue;
      }, {} as HighlightObject);
    }
    return {};
  })
);

const selectedActivePlayoffDriverVehicleNumbersSelectorFamily = atomFamily<
  { settingsId: string; numDriversToShow: number },
  Atom<HighlightObject>
>((params) =>
  atom<HighlightObject>((get) => {
    const settings = get(visualizationSettingsAtomFamily(params.settingsId));
    const activeSeriesId = get(activeSeriesIdSelector);

    if (settings && activeSeriesId) {
      const top4Color = getPlayoffDriversColor(4).id;
      const top8Color = getPlayoffDriversColor(8).id;
      const top12Color = getPlayoffDriversColor(12).id;
      const top16Color = getPlayoffDriversColor(16).id;
      const showActiveOnly = settings?.localVehicleSelections?.selectedPlayoffDrivers?.includes('active');
      const drivers = get(unwrappedDriverNamesSelector);
      const allPlayoffDrivers = get(playoffDriverAtomFamily(activeSeriesId));
      const playoffDrivers = showActiveOnly
        ? allPlayoffDrivers?.filter((driver) => driver.isContender).sort((a, b) => a.playoffRank - b.playoffRank)
        : allPlayoffDrivers
            ?.filter((driver) => driver.playoffRank <= params.numDriversToShow)
            .sort((a, b) => a.playoffRank - b.playoffRank);
      //the replace all is for MTJ showing up as Jr in some places and Jr. in others -_-
      const vehicleNumbers = playoffDrivers?.map(
        (playoffDriver) =>
          drivers?.find(
            (driver) => driver.driverFullName.replaceAll('.', '') === playoffDriver.driverName.replaceAll('.', '')
          )?.vehicleNumber
      );

      if (vehicleNumbers && vehicleNumbers?.length > 0) {
        return vehicleNumbers.reduce((highlightObject, currentValue, index) => {
          if (currentValue)
            highlightObject[currentValue] =
              index < 4 ? top4Color : index < 8 ? top8Color : index < 12 ? top12Color : top16Color;
          return highlightObject;
        }, {} as HighlightObject);
      }
    }
    return {};
  })
);

const selectedCurrentFastestDriverVehicleNumbersSelectorFamily = atomFamily<string, Atom<HighlightObject>>(
  (settingsId: string) =>
    atom<HighlightObject>((get) => {
      const settings = get(visualizationSettingsAtomFamily(settingsId));
      if (settings && settings?.localVehicleSelections?.selectedPerformances?.includes('currentFastestDriver')) {
        const dataSource = settings.dataFeedSelections || OriginType.ERDP;
        const vehicleNumber = get(currentFasterDriverSelectorFamily(dataSource));
        if (vehicleNumber.length > 0) {
          const currentFastestDriverColor = getPerformanceColor('currentFastestDriver').id;
          return {
            [vehicleNumber]: currentFastestDriverColor,
          } as HighlightObject;
        }
      }
      return {};
    })
);

export const selectedLuckyDogSelectorFamily = atomFamily<string, Atom<HighlightObject>>((settingsId: string) =>
  atom<HighlightObject>((get) => {
    const settings = get(visualizationSettingsAtomFamily(settingsId));
    if (settings) {
      const dataSource = get(activeSessionReplayEnabledSelector) ? OriginType.MULTILOOP : OriginType.ERDP;
      const vehicleNumber = get(lapDataLuckyDogSelectorFamily(dataSource));
      if (vehicleNumber) {
        const luckyDogColor = getPerformanceColor('luckyDog').id;
        const vehicle = [vehicleNumber];
        return vehicle.reduce((previousValue, currentValue) => {
          previousValue[currentValue] = luckyDogColor;
          return previousValue;
        }, {} as HighlightObject);
      }
    }
    return {};
  })
);

export const mobileSelectedVehicleAtom = atom<string>('');
export const mobileSettingsPanelOpenAtom = atom<boolean>(false);